import React, { useEffect, useState } from 'react';
import {
  BackArrowButton,
  BackArrowContainer,
  ButtonsContainer,
  HeaderContainer,
  HeaderDefaultContent,
  TitleContainer,
  AvatarContainer,
  NotificationsBellContainer,
} from './AppHeader.styled';
import { Body3 } from '../../typography';
import { ChevronLeft } from '../../icons/ChevronLeft';
import { useNavigate } from 'react-router-dom';
import { NotificationsBell } from '../NotificationsBell';
import Avatar from '../Avatar';
import { getItem, setItem } from '../../../../utils/localStorage';
import { unReadNotificationCount } from '../../../../requests/notifications';
import { sentryReport } from '../../../../helpers/sentry';
import { useTranslation } from 'react-i18next';
import { AuthMeRes } from '../../../../components/commons/NotificationBell/AuthMeRes';
import { getProfilePic } from '../../../../requests/profilepic';
import { GetAllDataByIndexedDB } from '../../../../indexed';
import { IAppHeader } from '../../../@types/appHeader';

export function AppHeader ({
  title,
  hasBackButton = true,
  children,
  navigateTo = '',
  onClick,
}: IAppHeader) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedHotel = getItem('selectedHotel');
  const notificationsCount = getItem('newNotificationsCounter');
  const [userProfilePicture, setUserProfilePicture] = useState<string>('');

  const getProfilePictureFromIndexedDB = async () => {
    const profilePicture = await GetAllDataByIndexedDB(
      'userProfilePic',
      selectedHotel
    );
    if (profilePicture) {
      setUserProfilePicture(profilePicture as string);
    }
  };

  useEffect(() => {
    const profilePicture = localStorage.getItem('userProfilePicture');
    if (profilePicture) {
      setUserProfilePicture(profilePicture);
    } else {
      getProfilePictureFromIndexedDB();
    }
    AuthMeRes()
      .then(() => {
        if (location.pathname !== '/notifications') {
          setUnreadNotificationsToLocalStorage();
        }
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          summaryError: t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_USER_DATA'),
          path: '/auth/me',
        });
      });

    if (!userProfilePicture) {
      getProfilePic()
        .then((data) => {
          if (data && data.length > 0 && data[0].url) {
            const img = new Image();
            img.src = data[0].url;
            img.onerror = () => {
              setUserProfilePicture('');
              localStorage.setItem('userProfilePicture', '');
              return;
            };
            setUserProfilePicture(data[0].url);
            localStorage.setItem('userProfilePicture', data[0].url);
          }
        })
        .catch((error) => {
          sentryReport(error.response.data, error.message, error.code, {
            summaryError: t(
              'COMMON.ALERT_MESSAGES.UNABLE_TO_GET_USER_PROFILE_PICTURE'
            ),
            path: '/user-profile/${userId}/profile/getAttachFile',
          });
        });
    }
  }, []);

  const setUnreadNotificationsToLocalStorage = () => {
    unReadNotificationCount()
      .then((response) => {
        setItem('newNotificationsCounter', response.data.count);
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          summaryError: t(
            'COMMON.ALERT_MESSAGES.UNABLE_TO_GET_NOTIFICATION_COUNT'
          ),
          path: '/actionPlan/getNotificationHistory',
        });
      });
  };

  return (
    <>
      <HeaderContainer data-testid="app-header">
        <HeaderDefaultContent>
          <BackArrowContainer data-testid="app-header-back-arrow">
            {hasBackButton && (
              <BackArrowButton>
                <ChevronLeft
                  onClick={() => {
                    if (onClick) {
                      onClick();
                    } else {
                      navigate(navigateTo || -1);
                    }
                  }}
                />
              </BackArrowButton>
            )}
          </BackArrowContainer>
          {title && (
            <TitleContainer>
              <Body3 color="white" data-testid="app-header-title">
                {title}
              </Body3>
            </TitleContainer>
          )}
          <ButtonsContainer>
            <NotificationsBellContainer>
              <NotificationsBell notificationsCount={notificationsCount} />
            </NotificationsBellContainer>
            <AvatarContainer
              onClick={() => {
                // TODO add a logic to show modal if data not saved
                navigate('/pilotwelcome');
              }}
            >
              <Avatar
                imageSrc={userProfilePicture}
                data-testid="app-header-avatar"
              />
            </AvatarContainer>
          </ButtonsContainer>
        </HeaderDefaultContent>
        {children}
      </HeaderContainer>
    </>
  );
}
